import { useState } from "react";
import { toast } from "sonner";
import { UserRound, Landmark } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const inputCls =
  "w-full bg-transparent border border-white/10 px-4 py-3 text-sm text-white placeholder:text-white/30 gold-focus transition-shadow";
const BANK_FIELDS = [
  ["account_name", "Account holder name"],
  ["bank_name", "Bank name"],
  ["account_number", "Account number"],
  ["ifsc", "IFSC code"],
  ["upi_id", "UPI ID (optional)"],
];

export const ProfileSection = () => {
  const { user, setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: user?.name || "", phone: user?.phone || "", bank: { account_name: "", bank_name: "", account_number: "", ifsc: "", upi_id: "", ...(user?.bank || {}) } });
  const bank = user?.bank || {};
  const hasBank = Boolean(bank.account_number || bank.upi_id);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch("/auth/profile", form);
      setUser(data);
      toast.success("Profile updated");
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mt-16 border border-white/10 bg-[#0A0A0A]" data-testid="profile-section">
      <div className="flex flex-wrap items-center justify-between gap-4 px-6 py-5 border-b border-white/10">
        <div className="flex items-center gap-3">
          <UserRound size={16} className="text-[#D4AF37]" />
          <div>
            <p className="text-sm text-white" data-testid="profile-name">{user?.name}</p>
            <p className="text-xs text-white/40" data-testid="profile-contact">{user?.email}{user?.phone ? ` · ${user.phone}` : ""}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-xs" data-testid="profile-bank-summary">
            <Landmark size={13} className={hasBank ? "text-emerald-400" : "text-yellow-400"} />
            {hasBank ? (
              <span className="text-white/60">Payouts to {bank.bank_name || "bank"} ····{(bank.account_number || bank.upi_id).slice(-4)}</span>
            ) : (
              <span className="text-yellow-400">Add payout bank details to receive returns</span>
            )}
          </div>
          <button data-testid="profile-edit-button" onClick={() => setOpen((o) => !o)} className="border border-white/20 px-4 py-2 font-accent text-[10px] tracking-[0.2em] uppercase text-white/70 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
            {open ? "Close" : hasBank ? "Edit Profile" : "Add Details"}
          </button>
        </div>
      </div>
      {open && (
        <form onSubmit={save} className="p-6 grid lg:grid-cols-2 gap-8" data-testid="profile-form">
          <div className="space-y-4">
            <p className="font-accent text-[10px] tracking-[0.25em] uppercase text-[#D4AF37]">Personal</p>
            <input data-testid="profile-name-input" required minLength={2} placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
            <input data-testid="profile-phone-input" placeholder="Phone number" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
            <p className="text-xs text-white/40">Email <span className="text-white/60">{user?.email}</span> cannot be changed.</p>
          </div>
          <div className="space-y-4">
            <p className="font-accent text-[10px] tracking-[0.25em] uppercase text-[#D4AF37]">Payout bank account</p>
            {BANK_FIELDS.map(([k, label]) => (
              <input key={k} data-testid={`profile-bank-${k.replace(/_/g, "-")}-input`} placeholder={label} value={form.bank[k]} onChange={(e) => setForm({ ...form, bank: { ...form.bank, [k]: e.target.value } })} className={inputCls} />
            ))}
            <button data-testid="profile-save-button" disabled={saving} className="w-full border border-[#D4AF37] text-[#D4AF37] py-3 font-accent text-[11px] tracking-[0.25em] uppercase hover:bg-[#D4AF37] hover:text-black transition-colors disabled:opacity-50">
              {saving ? "Saving…" : "Save Profile"}
            </button>
          </div>
        </form>
      )}
    </section>
  );
};
